import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fn } from '../api/functions';
import { EnvelopeIcon, CheckCircleIcon, ExclamationCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

export const InvitePage = () => {
  const { invitationId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Parse invitation ID to extract email (format: storyId_base64email_timestamp)
  const getEmailFromInvitationId = (inviteId) => {
    try {
      const parts = inviteId.split('_');
      if (parts.length >= 2) {
        const base64Email = parts[1];
        return Buffer.from(base64Email, 'base64').toString('utf-8');
      }
    } catch (e) {
      console.error('Error parsing invitation ID:', e);
    }
    return null;
  };

  // Parse story ID from invitation ID
  const getStoryIdFromInvitationId = (inviteId) => {
    try {
      const parts = inviteId.split('_');
      if (parts.length >= 1) {
        return parts[0];
      }
    } catch (e) {
      console.error('Error parsing invitation ID:', e);
    }
    return null;
  };

  useEffect(() => {
    const loadInvitation = async () => {
      try {
        setLoading(true);
        setError(null);

        const email = getEmailFromInvitationId(invitationId);
        const storyId = getStoryIdFromInvitationId(invitationId);

        if (!email || !storyId) {
          setError('Invalid invitation link');
          setLoading(false);
          return;
        }

        // Get invitation details
        const result = await fn.getStoryInvitationDetails({
          storyId,
          email,
          token: invitationId
        });

        if (result.data.success) {
          setInvitation(result.data.invitation);
        } else {
          setError('Invitation not found');
        }
      } catch (err) {
        console.error('Error loading invitation:', err);
        setError(err.message || 'Failed to load invitation');
      } finally {
        setLoading(false);
      }
    };

    if (invitationId) {
      loadInvitation();
    }
  }, [invitationId]);

  const handleAcceptInvitation = async () => {
    if (!currentUser) {
      // User needs to log in first
      navigate('/login', { state: { from: `/invite/${invitationId}` } });
      return;
    }

    try {
      setAccepting(true);
      setError(null);

      const email = getEmailFromInvitationId(invitationId);
      const storyId = getStoryIdFromInvitationId(invitationId);

      // Verify user email matches invitation email
      if (currentUser.email !== email) {
        setError(`This invitation was sent to ${email}. Please log in with that email address.`);
        setAccepting(false);
        return;
      }

      const result = await fn.acceptStoryInvitation({
        storyId,
        invitationEmail: email,
        invitationToken: invitationId
      });

      if (result.data.success) {
        setSuccess(true);
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setError('Failed to accept invitation');
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(err.message || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ms-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8">
          <div className="text-center">
            <ExclamationCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="memory-stream-button w-full"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8">
          <div className="text-center">
            <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Invitation Accepted!</h1>
            <p className="text-gray-600 mb-4">
              You now have access to <strong>{invitation?.storyTitle}</strong>
            </p>
            <p className="text-sm text-gray-500">
              Redirecting to your dashboard...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Check if invitation is expired
  const isExpired = invitation?.expiresAt && new Date(invitation.expiresAt._seconds * 1000) < new Date();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-ms-primary to-ms-secondary px-8 py-6 text-white">
          <EnvelopeIcon className="h-12 w-12 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-center">Story Invitation</h1>
        </div>

        {/* Content */}
        <div className="p-8">
          {isExpired ? (
            <div className="text-center">
              <ClockIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Invitation Expired</h2>
              <p className="text-gray-600 mb-6">
                This invitation has expired. Please request a new invitation from the story owner.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  {invitation?.sharedByName} has invited you to view their story
                </h2>
                
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Story:</span>
                      <p className="text-lg font-semibold text-gray-900">{invitation?.storyTitle}</p>
                    </div>
                    
                    <div>
                      <span className="text-sm font-medium text-gray-500">Access Level:</span>
                      <p className="text-gray-900 capitalize">
                        {invitation?.accessLevel === 'viewer' ? (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                            👁️ Viewer - Can read and listen
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                            ✏️ Editor - Can read, listen, and help edit
                          </span>
                        )}
                      </p>
                    </div>

                    {invitation?.storyType && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">Story Type:</span>
                        <p className="text-gray-900 capitalize">
                          {invitation.storyType === 'gift' && '🎁 Gift Story'}
                          {invitation.storyType === 'collaborative' && '👥 Collaborative Story'}
                          {invitation.storyType === 'individual' && '📖 Individual Story'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {invitation?.message && (
                  <div className="bg-ms-accent-green bg-opacity-10 border border-ms-accent-green border-opacity-30 rounded-lg p-4 mb-6">
                    <p className="text-sm font-medium text-gray-700 mb-1">Personal Message:</p>
                    <p className="text-gray-800 italic">"{invitation.message}"</p>
                  </div>
                )}
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                {!currentUser ? (
                  <>
                    <button
                      onClick={handleAcceptInvitation}
                      className="memory-stream-button w-full"
                    >
                      Sign In to Accept Invitation
                    </button>
                    <p className="text-center text-sm text-gray-600">
                      Don't have an account?{' '}
                      <button
                        onClick={() => navigate('/signup', { state: { from: `/invite/${invitationId}` } })}
                        className="text-ms-secondary font-medium hover:underline"
                      >
                        Sign up here
                      </button>
                    </p>
                  </>
                ) : (
                  <>
                    {invitation?.status === 'accepted' ? (
                      <div className="text-center">
                        <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-3" />
                        <p className="text-gray-700 mb-4">You've already accepted this invitation</p>
                        <button
                          onClick={() => navigate('/')}
                          className="memory-stream-button w-full"
                        >
                          Go to Dashboard
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={handleAcceptInvitation}
                        disabled={accepting}
                        className="memory-stream-button w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {accepting ? 'Accepting...' : 'Accept Invitation'}
                      </button>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvitePage;
